import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import WhatsAppButton from "./WhatsAppButton";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://bum-desa-warga-dekat-bandung-production.up.railway.app";

const API_BASE = `${API_BASE_URL}/api/news`;

const initialForm = {
  title: "",
  slug: "",
  summary: "",
  body: "",
  cover_url: "",
  status: "draft",
  published_at: "",
};

function formatTanggal(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";

  return date.toLocaleString("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminNews() {
  const navigate = useNavigate();

  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [form, setForm] = useState(initialForm);

  const token = useMemo(() => {
    return (
      localStorage.getItem("token") || localStorage.getItem("adminToken") || ""
    );
  }, []);

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    navigate("/admin/login", { replace: true });
  };

  const loadNews = useCallback(async () => {
    if (!token) {
      setLoading(false);
      navigate("/admin/login", { replace: true });
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const params = new URLSearchParams({
        page: "1",
        pageSize: "50",
        sort: "latest",
        status: filterStatus,
        includeDeleted: showDeleted ? "true" : "false",
        q: keyword,
      });

      const res = await fetch(`${API_BASE}/admin?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      console.log("ADMIN NEWS RESPONSE:", data);

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Token tidak valid / sesi admin tidak dikenali API");
        }

        throw new Error(
          data.error || data.message || "Gagal mengambil data berita admin",
        );
      }

      if (Array.isArray(data.data)) {
        setNewsList(data.data);
      } else if (Array.isArray(data.news)) {
        setNewsList(data.news);
      } else if (Array.isArray(data)) {
        setNewsList(data);
      } else {
        setNewsList([]);
      }
    } catch (err) {
      setError(err.message || "Terjadi kesalahan saat mengambil berita");
      setNewsList([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, showDeleted, keyword, token, navigate]);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        summary: form.summary || null,
        body: form.body,
        cover_url: form.cover_url || null,
        status: form.status,
        published_at: form.published_at || null,
      };

      const url = editingId ? `${API_BASE}/${editingId}` : API_BASE;
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      console.log("SAVE NEWS RESPONSE:", data);

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Token tidak valid / sesi admin tidak dikenali API");
        }

        throw new Error(data.error || data.message || "Gagal menyimpan berita");
      }

      setMessage(
        editingId
          ? "Berita berhasil diperbarui."
          : "Berita berhasil ditambahkan.",
      );
      resetForm();
      loadNews();
    } catch (err) {
      setError(err.message || "Terjadi kesalahan saat menyimpan berita");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      slug: item.slug || "",
      summary: item.summary || "",
      body: item.body || "",
      cover_url: item.cover_url || "",
      status: item.status || "draft",
      published_at: item.published_at
        ? new Date(item.published_at).toISOString().slice(0, 16)
        : "",
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Yakin ingin menghapus berita ini?");
    if (!ok) return;

    setError("");
    setMessage("");

    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      console.log("DELETE NEWS RESPONSE:", data);

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Token tidak valid / sesi admin tidak dikenali API");
        }

        throw new Error(data.error || data.message || "Gagal menghapus berita");
      }

      setMessage("Berita berhasil dihapus.");
      loadNews();
    } catch (err) {
      setError(err.message || "Terjadi kesalahan saat menghapus berita");
    }
  };

  const handleRestore = async (id) => {
    setError("");
    setMessage("");

    try {
      const res = await fetch(`${API_BASE}/${id}/restore`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      console.log("RESTORE NEWS RESPONSE:", data);

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Token tidak valid / sesi admin tidak dikenali API");
        }

        throw new Error(
          data.error || data.message || "Gagal me-restore berita",
        );
      }

      setMessage("Berita berhasil direstore.");
      loadNews();
    } catch (err) {
      setError(err.message || "Terjadi kesalahan saat restore berita");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadNews();
  };

  return (
    <div className="min-h-screen bg-[#f8faf8] font-serif">
      <Navbar />
      <WhatsAppButton />

      <section className="pt-32 pb-12 px-6 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-3">
              Dashboard Admin
            </p>
            <h1 className="text-4xl text-[#3d4f45] font-light mb-3">
              Kelola Berita
            </h1>
            <p className="text-sm text-gray-500 max-w-2xl">
              Tambah, edit, hapus, dan restore berita dari dashboard admin.
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <NavLink
                to="/admin/posts"
                className={({ isActive }) =>
                  `px-4 py-2 rounded-xl text-sm transition border ${
                    isActive
                      ? "bg-[#3d4f45] text-white border-[#3d4f45]"
                      : "border-gray-300 text-[#3d4f45] hover:bg-[#f1f5f2]"
                  }`
                }
              >
                Kelola Berita
              </NavLink>

              <NavLink
                to="/admin/products"
                className={({ isActive }) =>
                  `px-4 py-2 rounded-xl text-sm transition border ${
                    isActive
                      ? "bg-[#3d4f45] text-white border-[#3d4f45]"
                      : "border-gray-300 text-[#3d4f45] hover:bg-[#f1f5f2]"
                  }`
                }
              >
                Kelola Produk
              </NavLink>
            </div>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className="h-fit rounded-xl border border-red-300 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition"
          >
            Logout
          </button>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.05fr_1.4fr] gap-8">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 h-fit">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl text-[#3d4f45] font-light">
                {editingId ? "Edit Berita" : "Tambah Berita"}
              </h2>
              {editingId && (
                <button
                  onClick={resetForm}
                  className="text-sm text-gray-500 hover:text-[#3d4f45]"
                  type="button"
                >
                  Batal Edit
                </button>
              )}
            </div>

            {message && (
              <div className="mb-4 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                {message}
              </div>
            )}

            {error && (
              <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-[#3d4f45] mb-2">
                  Judul
                </label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#3d4f45]"
                  placeholder="Masukkan judul berita"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-[#3d4f45] mb-2">
                  Slug
                </label>
                <input
                  type="text"
                  name="slug"
                  value={form.slug}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#3d4f45]"
                  placeholder="Opsional, biarkan kosong untuk auto-generate"
                />
              </div>

              <div>
                <label className="block text-sm text-[#3d4f45] mb-2">
                  Ringkasan
                </label>
                <textarea
                  name="summary"
                  value={form.summary}
                  onChange={handleChange}
                  rows="3"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#3d4f45]"
                  placeholder="Ringkasan singkat berita"
                />
              </div>

              <div>
                <label className="block text-sm text-[#3d4f45] mb-2">
                  Isi Berita
                </label>
                <textarea
                  name="body"
                  value={form.body}
                  onChange={handleChange}
                  rows="8"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#3d4f45]"
                  placeholder="Tulis isi lengkap berita"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-[#3d4f45] mb-2">
                  Cover URL
                </label>
                <input
                  type="text"
                  name="cover_url"
                  value={form.cover_url}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#3d4f45]"
                  placeholder="https://..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#3d4f45] mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#3d4f45]"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-[#3d4f45] mb-2">
                    Published At
                  </label>
                  <input
                    type="datetime-local"
                    name="published_at"
                    value={form.published_at}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#3d4f45]"
                  />
                </div>
              </div>

              <div className="pt-3 flex gap-3 flex-wrap">
                <button
                  type="submit"
                  disabled={saving || !token}
                  className="bg-[#3d4f45] text-white px-6 py-3 rounded-xl text-sm hover:bg-[#4d6156] transition disabled:opacity-50"
                >
                  {saving
                    ? "Menyimpan..."
                    : editingId
                      ? "Simpan Perubahan"
                      : "Tambah Berita"}
                </button>

                <button
                  type="button"
                  onClick={resetForm}
                  className="border border-gray-300 text-gray-600 px-6 py-3 rounded-xl text-sm hover:bg-gray-50 transition"
                >
                  Reset
                </button>
              </div>
            </form>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl text-[#3d4f45] font-light mb-2">
                  Daftar Berita
                </h2>
                <p className="text-sm text-gray-500">
                  Kelola berita yang sudah dibuat admin.
                </p>
              </div>

              <form onSubmit={handleSearch} className="flex gap-3 flex-wrap">
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="Cari judul / isi berita"
                  className="border border-gray-300 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#3d4f45]"
                />

                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border border-gray-300 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#3d4f45]"
                >
                  <option value="all">Semua Status</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>

                <label className="flex items-center gap-2 px-3 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                  />
                  Tampilkan deleted
                </label>

                <button
                  type="submit"
                  className="bg-[#3d4f45] text-white px-5 py-3 rounded-xl text-sm hover:bg-[#4d6156] transition"
                >
                  Cari
                </button>
              </form>
            </div>

            {loading ? (
              <p className="text-gray-500">Loading berita admin...</p>
            ) : newsList.length === 0 ? (
              <p className="text-gray-500">Belum ada data berita.</p>
            ) : (
              <div className="space-y-4">
                {newsList.map((item) => (
                  <div
                    key={item.id}
                    className="border border-gray-200 rounded-2xl p-5 hover:shadow-sm transition"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-xs px-3 py-1 rounded-full bg-[#eef4ef] text-[#3d4f45]">
                            {item.status || "draft"}
                          </span>

                          {item.deleted_at && (
                            <span className="text-xs px-3 py-1 rounded-full bg-red-50 text-red-600 border border-red-200">
                              deleted
                            </span>
                          )}

                          <span className="text-xs text-gray-400">
                            {formatTanggal(item.published_at)}
                          </span>
                        </div>

                        <h3 className="text-lg text-[#3d4f45] font-semibold mb-1">
                          {item.title}
                        </h3>

                        <p className="text-xs text-gray-400 mb-2">
                          slug: {item.slug}
                        </p>

                        <p className="text-sm text-gray-600 line-clamp-2">
                          {item.summary || "Tidak ada ringkasan."}
                        </p>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        {!item.deleted_at && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className="px-4 py-2 rounded-xl border border-[#3d4f45] text-[#3d4f45] text-sm hover:bg-[#3d4f45] hover:text-white transition"
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(item.id)}
                              className="px-4 py-2 rounded-xl border border-red-300 text-red-600 text-sm hover:bg-red-50 transition"
                            >
                              Hapus
                            </button>
                          </>
                        )}

                        {item.deleted_at && (
                          <button
                            type="button"
                            onClick={() => handleRestore(item.id)}
                            className="px-4 py-2 rounded-xl border border-green-300 text-green-700 text-sm hover:bg-green-50 transition"
                          >
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
